/* eslint-disable simple-import-sort/imports */
import { getLocalStorageItem } from 'hooks';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAPI } from 'services/axios';
import useToast from 'services/responseToast';
import { queryKeys } from '../constants';

type FetchInboxListParams = {
  data: any;
  uuid: string;
};
const userDetails = getLocalStorageItem('roles');

const appendQueryParams = (url: string, data: Record<string, any>): string => {
  let resp = `${url}?`;
  if (data) {
    Object.keys(data).forEach((key) => {
      if (data[key] || typeof data[key] === 'boolean') {
        resp += `${key}=${
          typeof data[key] === 'string'
            ? data[key]?.replaceAll('&', '%26')
            : data[key]
        }&`;
      }
    });
  }
  return resp;
};

const patientsList = (data: any) => {
  const { userListFilters, userParams, headerKeys } = data;
  const queryParams: Record<string, any> = {
    ...userListFilters,
    ...userParams,
  };

  return useAPI({
    method: 'GET',
    url: appendQueryParams(`patients`, queryParams),
    headers: {
      'x-klynex-access-key': headerKeys,
    },
  });
};

export const patientsListApiQuery = (data: any) => {
  return useQuery([queryKeys.PATIENT_LIST, data], () => patientsList(data), {
    enabled: true,
  });
};

// Create Patient User APi//
const createPatientApi = (payload: any, headers: Record<string, string>) => {
  return useAPI({
    method: 'POST',
    url: 'patients',
    data: payload,
    headers: {
      ...headers,
    },
  });
};

export const usecreatePatientApiQuery = (headers: Record<string, string>) => {
  const queryClient = useQueryClient();
  return useMutation((payload) => createPatientApi(payload, headers), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.PATIENT_LIST]);
    },
  });
};

const getSelectedPatientData = (
  data: any,
  headers: Record<string, string>,
  accountId: string
) => {
  return useAPI({
    method: 'GET',
    url: `patients/${data.uuid}?accountId=${accountId}`,
    headers: {
      ...headers,
    },
  });
};

export const useGetSelectedPatientDataApiQuery = (
  data: any,
  customHeaders: Record<string, string>,
  accountId: string
) => {
  return useQuery(
    [queryKeys.SELLECTED_PATIENT, data.uuid, accountId],
    () => getSelectedPatientData(data, customHeaders, accountId),
    {
      enabled: true,
    }
  );
};

// Update patient api //
const updatePatientApi = (payload: any, headers: Record<string, string>) => {
  const { uuid, data } = payload;
  return useAPI({
    method: 'PATCH',
    url: `patients/${uuid}`,
    data,
    headers: {
      ...headers,
    },
  });
};

export const useUpdatePatientApiQuery = (headers: Record<string, string>) => {
  const queryClient = useQueryClient();
  return useMutation((payload) => updatePatientApi(payload, headers), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.PATIENT_LIST]);
    },
  });
};

// Get all blast groups
const getBlastGroupData = (accountId: any) => {
  return useAPI({
    method: 'GET',
    url: `groups/has-blast?accountId=${accountId}`,
  });
};

export const useGetBlastGroupDataDataApiQuery = (accountId: any) => {
  return useQuery(
    [queryKeys.BLAST_LIST, accountId],
    () => getBlastGroupData(accountId),
    {
      enabled: true,
    }
  );
};

// Add new request from patient user APi
const addNewRequestApi = async ({
  formData,
  userPayload,
}: {
  formData: any;
  userPayload: any;
}) => {
  const url = `requests?topicId=${
    userPayload.topicId
  }&description=${encodeURIComponent(userPayload.description)}`;
  return useAPI({
    method: 'POST',
    url,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const useAddNewRequestApiQuery = () => {
  const queryClient = useQueryClient();
  return useMutation(addNewRequestApi, {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.REQUEST_LIST]);
    },
  });
};

// Request list data

const requestList = (data: any) => {
  const { userListFilters, userParams } = data;
  const queryParams: Record<string, any> = {
    ...userListFilters,
    ...userParams,
  };

  return useAPI({
    method: 'GET',
    url: appendQueryParams(`requests`, queryParams),
  });
};

export const requestListApiQuery = (data: any) => {
  return useQuery([queryKeys.REQUEST_LIST, data], () => requestList(data), {
    enabled: true,
    // refetchInterval: 30000,
  });
};

// Update new requests api //
const updateRequestApi = (payload: any) => {
  const { uuid, data } = payload;
  return useAPI({
    method: 'PATCH',
    url: `requests/${uuid}`,
    data,
  });
};

export const useUpdateRequestApiQuery = () => {
  const queryClient = useQueryClient();
  return useMutation(updateRequestApi, {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.INBOX_LIST]);
      queryClient.invalidateQueries([queryKeys.REQUEST_LIST]);
    },
  });
};

const getAllTopicsListApi = (data: any) => {
  const { userListFilters, userParams, accountsid } = data;
  const queryParams: Record<string, any> = {
    ...userListFilters,
    ...userParams,
  };

  return useAPI({
    method: 'GET',
    url: appendQueryParams(`accounts/${accountsid}/topics`, queryParams),
  });
};
export const useAllTopicsListApiQuery = (data: any) => {
  return useQuery(
    [queryKeys.TOPIC_LIST, data],
    () => getAllTopicsListApi(data),
    {
      enabled: true,
    }
  );
};

const fetchInboxList = ({ data, uuid }: FetchInboxListParams) => {
  const { userListFilters, userParams } = data;
  const queryParams: Record<string, any> = {
    ...userListFilters,
    ...userParams,
  };

  return useAPI({
    method: 'GET',
    url: appendQueryParams(`users/${uuid}/inbox`, queryParams),
  });
};

export const useInboxListQuery = (data: any, uuid: string) => {
  return useQuery(
    [queryKeys.INBOX_LIST, data],
    () => fetchInboxList({ data, uuid }),
    {
      enabled: true,
      // refetchInterval: 30000,
    }
  );
};

interface AddTopicPayload {
  accountId: string;
  topics: string[];
}
interface DisabledMobileNotificationPayload {
  accountId: string;
  isMobileNotificationEnabled: boolean;
}
interface ChangePatientStatusPayload {
  accountId: string;
  uuid: string;
}

const addNewTopicApi = (payload: AddTopicPayload) => {
  return useAPI({
    method: 'POST',
    url: `accounts/${payload.accountId}/topics`,
    data: { topics: payload.topics },
  });
};

export const useAddNewTopicMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(addNewTopicApi, {
    onSuccess: () => {
      queryClient.invalidateQueries('TOPIC_LIST');
    },
  });
};

// disable mobile notification
const disableMobileNotificationApi = (
  payload: DisabledMobileNotificationPayload
) => {
  return useAPI({
    method: 'PATCH',
    url: `accounts/disable-mobile-notifications/${payload.accountId}?isMobileNotificationEnabled=${payload?.isMobileNotificationEnabled}`,
  });
};

export const usedisableMobileNotificationApi = () => {
  const queryClient = useQueryClient();
  return useMutation(disableMobileNotificationApi, {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.AUTH_ME]);
    },
  });
};

// Update Topic name API
const updateTopicNameApi = (payload: {
  accountId: string;
  topicId: string;
  data: { name: string; description: string | null };
}) => {
  const { accountId, topicId, data } = payload;
  return useAPI({
    method: 'PATCH',
    url: `accounts/${accountId}/topics/${topicId}`,
    data,
  });
};

export const useUpdateTopicNameMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(updateTopicNameApi, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.TOPIC_LIST);
    },
  });
};

//  Assign Topic name API
const assignTopicNameApi = (payload: {
  accountId: string;
  topicId: string;
  data: { ids: number[]; action: string };
}) => {
  const { accountId, topicId, data } = payload;
  return useAPI({
    method: 'POST',
    url: `accounts/${accountId}/topics/${topicId}/assign`,
    data,
  });
};

export const useAssignTopicNameApiMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(assignTopicNameApi, {
    onSuccess: () => {
      queryClient.invalidateQueries('TOPIC_LIST');
    },
  });
};

const reclassifyTopicApi = (payload: any) => {
  return useAPI({
    method: 'PATCH',
    url: `requests/${payload.uuid}/reclassify`,
    data: payload.topics,
  });
};

export const useReclassifyTopicMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(reclassifyTopicApi, {
    onSuccess: () => {
      queryClient.invalidateQueries('INBOX_LIST');
      queryClient.invalidateQueries('REQUEST_LIST');
    },
  });
};

//  Assign Topic name API
const forwaordTopicApi = (payload: {
  uuid: string;
  data: { ids: number[]; tunnel: string };
}) => {
  const { uuid, data } = payload;
  return useAPI({
    method: 'PATCH',
    url: `requests/${uuid}/forward`,
    data,
  });
};

export const useforwaordTopicApiMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(forwaordTopicApi, {
    onSuccess: () => {
      queryClient.invalidateQueries('TOPIC_LIST');
      queryClient.invalidateQueries('INBOX_LIST');
      queryClient.invalidateQueries('REQUEST_LIST');
    },
  });
};

// Messaging freeze channel
const freezeChannelApi = (payload: any) => {
  const { channelType, channelId, isFreeze } = payload;

  const queryString = new URLSearchParams({
    channelType,
    channelId,
    isFreeze: String(isFreeze),
  }).toString();

  return useAPI({
    method: 'PATCH',
    url: `channels/freeze-chat?${queryString}`,
  });
};

export const useFreezeChannelApiQuery = () => {
  const queryClient = useQueryClient();
  return useMutation(freezeChannelApi, {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.PATIENT_LIST]);
    },
  });
};

const ChangePatientStatusApi = (
  payload: ChangePatientStatusPayload,
  headers: Record<string, string>
) => {
  return useAPI({
    method: 'PATCH',
    url: `patients/change-onboarding-status/${payload.uuid}?accountId=${payload.accountId}`,
    headers: {
      ...headers,
    },
  });
};

export const useChangePatientStatusApi = (headers: Record<string, string>) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: ChangePatientStatusPayload) =>
      ChangePatientStatusApi(payload, headers),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.AUTH_ME]);
      },
    }
  );
};

//  Notification Configuration for Topics
const notificationConfigTopicApi = (payload: {
  accountId: string;
  topicId: string;
  data: { preferences: string[] };
}) => {
  const { accountId, topicId, data } = payload;
  return useAPI({
    method: 'POST',
    url: `accounts/${accountId}/topics/disable-topic-notification/${topicId}`,
    data,
  });
};

export const useNotificationConfigTopicApiMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(notificationConfigTopicApi, {
    onSuccess: () => {
      queryClient.invalidateQueries('TOPIC_LIST');
    },
  });
};

const CreateChannelInboxReply = (data: any) => {
  const method = 'POST';
  const url = '/channels';
  return useAPI({
    method,
    url,
    data: { ...data },
  });
};

export const useCreateChannelInboxReplyQuery = () => {
  return useMutation(CreateChannelInboxReply);
};

// disable topics in settings
const disableTopicApi = (payload: any) => {
  return useAPI({
    method: 'PATCH',
    url: `accounts/${payload.accountId}/topics/toggle/${payload?.topicId}?isActive=${payload.isActive}`,
    data: payload,
  });
};

export const usedisableTopicApi = () => {
  const queryClient = useQueryClient();
  return useMutation(disableTopicApi, {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.TOPIC_LIST]);
    },
  });
};

// Deactivate Patient Account
const DeactivatePatientAcconutApi = (
  payload: ChangePatientStatusPayload,
  headers: Record<string, string>
) => {
  return useAPI({
    method: 'PATCH',
    url: `patients/deactivate-patient/${payload.uuid}?accountId=${payload.accountId}`,
    headers: {
      ...headers,
    },
  });
};

export const useDeactivatePatientAcconutApi = (
  headers: Record<string, string>
) => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: ChangePatientStatusPayload) =>
      DeactivatePatientAcconutApi(payload, headers),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.AUTH_ME]);
      },
    }
  );
};

// Deactivate Staff User Account
const DeactivateStaffUserAcconutApi = (payload: ChangePatientStatusPayload) => {
  return useAPI({
    method: 'PATCH',
    url: `users/deactivate-user/${payload.uuid}`,
  });
};

export const useDeactivateStaffUserAcconutApi = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: ChangePatientStatusPayload) =>
      DeactivateStaffUserAcconutApi(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.AUTH_ME]);
      },
    }
  );
};

// Delete Patient Account
const DeletePatientAcconutApi = (
  payload: any,
  headers: Record<string, string>
) => {
  return useAPI({
    method: 'PATCH',
    url: `patients/delete-info/${payload.password}?accountId=${payload.accountId}`,
    headers: {
      ...headers,
    },
  });
};

export const useDeletePatientAcconutApi = (headers: Record<string, string>) => {
  return useMutation((payload: ChangePatientStatusPayload) =>
    DeletePatientAcconutApi(payload, headers)
  );
};

// Archieve requests api //
const archieveRequestApi = (payload: any) => {
  const { uuid, isArchived } = payload;
  const url = `requests/${uuid}/archive?isArchived=${isArchived}`;
  return useAPI({
    method: 'PATCH',
    url,
  });
};

export const useArchieveRequestApiQuery = () => {
  const queryClient = useQueryClient();
  return useMutation(archieveRequestApi, {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.INBOX_LIST]);
      queryClient.invalidateQueries('REQUEST_LIST');
    },
  });
};

const unreadUserApi = (payload: { data: { userUuids: string[] } }) => {
  const { data } = payload;
  return useAPI({
    method: 'POST',
    url: `users/unread-message`,
    data,
  });
};

export const useUnreadUserApiMutation = () => {
  return useMutation(unreadUserApi);
};

// Update patient pass api

const updatePatientPass = (payload: any) => {
  const { uuid, data } = payload;
  return useAPI({
    method: 'PATCH',
    url: `users/account-credentials/${uuid}`,
    data,
  });
};

export const useupdatePatientPassApiQuery = () => {
  return useMutation((payload) => updatePatientPass(payload));
};

// Update Document name API
const updateDocumentNameApi = (payload: {
  uuid: string;
  data: { documentId: number; documentName: string };
}) => {
  const { uuid, data } = payload;
  return useAPI({
    method: 'PATCH',
    url: `users/update-user-documents/${uuid}?documentName=${data.documentName}&documentId=${data.documentId}`,
    data,
  });
};

// Upload Documents

export const useUpdateDocumentNameMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(updateDocumentNameApi, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.USER_DOCUMENTS);
    },
  });
};

const uploadDocuments = ({
  uuid,
  files,
  guardian_uuid = null,
}: {
  uuid: string;
  files: File[];
  guardian_uuid?: string | null;
}) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  return useAPI({
    method: 'PATCH',
    url: `/users/${uuid}/upload?guardian_uuid=${guardian_uuid}`,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const useUploadDocuments = () => {
  const query = useQueryClient();
  const responseToast = useToast();
  return useMutation(uploadDocuments, {
    onSuccess: () => {
      query.invalidateQueries([queryKeys.USER_DOCUMENTS]);
      responseToast.toastSuccess('Document upload successfully');
    },
  });
};

// FETCH DOCUMENTS
const fetchDocuments = (data: any) => {
  const { userListFilters, userParams, uuid } = data;
  const queryParams: Record<string, any> = {
    ...userListFilters,
    ...userParams,
  };

  return useAPI({
    method: 'GET',
    url: appendQueryParams(`/users/user-documents/${uuid}`, queryParams),
  });
};

export const useFetchDocuments = (data: any) => {
  return useQuery([queryKeys.USER_DOCUMENTS, data], () => fetchDocuments(data));
};

// DELETE DOCUMENTS
const deleteDocument = ({
  uuid,
  documentId,
}: {
  uuid: string;
  documentId: string;
}) => {
  return useAPI({
    method: 'DELETE',
    url: `/users/delete-user-documents/${uuid}?documentId=${documentId}`,
  });
};

export const useDeleteDocument = () => {
  const query = useQueryClient();
  return useMutation(deleteDocument, {
    onSuccess: () => query.invalidateQueries([queryKeys.USER_DOCUMENTS]),
  });
};

interface GetEncryptedUrlParams {
  uuid: any;
  documentId: any;
}

interface UseGetEncryptedUrlApiQueryParams {
  uuid: any;
  documentId: any;
}

export const getEncryptedUrl = ({
  uuid,
  documentId,
}: GetEncryptedUrlParams) => {
  return useAPI({
    method: 'GET',
    url: `users/user-document-url/${uuid}?documentId=${documentId}`,
  });
};

export const useGetEncryptedUrlApiQuery = ({
  uuid,
  documentId,
}: UseGetEncryptedUrlApiQueryParams) => {
  return useQuery(
    [queryKeys.ENCRYPTED_URL, uuid, documentId],
    () => getEncryptedUrl({ uuid, documentId }),
    {
      enabled: true,
    }
  );
};

const getEncryptedUrlDownload = ({
  uuid,
  documentId,
}: GetEncryptedUrlParams) => {
  return useAPI({
    method: 'GET',
    url: `users/user-document-url/${uuid}?documentId=${documentId}`,
  });
};

export const useGetEncryptedUrlApiDownloadQuery = ({
  uuid,
  documentId,
}: UseGetEncryptedUrlApiQueryParams) => {
  return useQuery(
    [queryKeys.ENCRYPTED_URL, uuid, documentId],
    () => getEncryptedUrlDownload({ uuid, documentId }),
    {
      enabled: !!uuid && !!documentId,
    }
  );
};

// FETCH Request DOCUMENTS
const fetchRequestDocuments = (data: any) => {
  const { userListFilters, userParams, uuid } = data;
  const queryParams: Record<string, any> = {
    ...userListFilters,
    ...userParams,
  };

  return useAPI({
    method: 'GET',
    url: `/requests/${uuid}`,
  });
};

export const useFetchRequestDocuments = (data: any) => {
  return useQuery([queryKeys.USER_DOCUMENTS, data], () =>
    fetchRequestDocuments(data)
  );
};

const deleteRequestDocument = ({
  uuid,
  documentId,
}: {
  uuid: string;
  documentId: string;
}) => {
  return useAPI({
    method: 'DELETE',
    url: `/users/delete-user-documents/${uuid}?documentId=${documentId}`,
  });
};

export const useDeleteRequestDocument = () => {
  const query = useQueryClient();
  return useMutation(deleteRequestDocument, {
    onSuccess: () => query.invalidateQueries([queryKeys.REQUEST_DOCUMENTS]),
  });
};

// upload CSV filess
const uploadCSV = ({
  accountId,
  file,
  accesskey,
}: {
  accountId: any;
  file: any;
  accesskey: string;
}) => {
  const formData = new FormData();
  formData.append('file', file);

  return useAPI({
    url: `/patients/upload-csv-file?accountId=${accountId}`,
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
      'x-klynex-access-key': accesskey,
    },
    data: formData,
  });
};

export const useUploadCSV = () => {
  const query = useQueryClient();
  return useMutation(uploadCSV, {
    onSuccess: () => query.invalidateQueries(queryKeys.PATIENT_LIST),
  });
};

const updateUsersData = ({
  data,
  uuid,
  accesskey,
}: {
  data: Object;
  uuid: string;
  accesskey: string;
}) => {
  return useAPI({
    method: 'PATCH',
    headers: {
      'x-klynex-access-key': accesskey,
    },
    url: `patients/update-duplicate-patient/${uuid}`,
    data: { data },
  });
};

export const useUpdateUserData = () => {
  return useMutation(updateUsersData);
};

const updateUserStatus = ({
  uuid,
}: // accesskey,
{
  uuid: string;
  // accesskey: string;
}) => {
  return useAPI({
    method: 'PATCH',
    // headers: {
    //   'x-klynex-access-key': accesskey,
    // },
    url: `users/${uuid}/inviteUser`,
  });
};

export const useUpdateUserStatus = () => {
  return useMutation(updateUserStatus);
};

const activateAccount = ({
  firstName,
  lastName,
  dob,
}: {
  firstName: string;
  lastName: string;
  dob: string;
}) => {
  return useAPI({
    method: 'POST',
    url: `users/activation/fetchUserByDob`,
    data: { firstName, lastName, dob },
  });
};

export const useActivateAccount = () => {
  return useMutation(activateAccount);
};

const chooseActivationClinic = ({
  firstName,
  lastName,
  dob,
  accountId,
}: {
  firstName: string;
  lastName: string;
  dob: string | null;
  accountId: string | null;
}) => {
  return useAPI({
    method: 'POST',
    url: `users/activation/resendNotifications`,
    data: { firstName, lastName, dob, accountId },
  });
};

export const useChooseActivationClinic = () => {
  return useMutation(chooseActivationClinic);
};

const searchDependentUser = ({
  accountId,
  searchString,
  uuid = null,
}: {
  accountId: number;
  searchString: string | null;
  uuid?: string | null;
}) => {
  return useAPI({
    method: 'GET',
    url: `users/findByName?searchString=${searchString}&accountId=${accountId}&uuid=${uuid}`,
  });
};

export const useSearchDependentUser = ({
  accountId,
  searchString,
  uuid = null,
}: {
  accountId: number;
  searchString: string | null;
  uuid?: string | null;
}) => {
  return useQuery([queryKeys.DEPENDENT_USERS, accountId, searchString], () =>
    searchDependentUser({ accountId, searchString, uuid })
  );
};

interface createDependentProp {
  firstname: string;
  lastname: string;
  dob: string;
  accountId: number;
  patientId: number;
  email: string;
  phone: string;
  uuid: string;
}
// "firstname": "Adam",
// "lastname": "fin",
// "accountId": 23000,
// "patientId": null,
// "emrId": null,
// "internalLink": null,
// "externalLink": null,
// "email": "ademfin@klynex.com",
// "phone": "+919999999999",
// "guardian_uuid": "string",
// "dependent_uuid": "string"

const createDependent = ({
  firstname,
  lastname,
  dob,
  accountId,
  patientId,
  email,
  phone,
  uuid,
}: createDependentProp) => {
  return useAPI({
    method: 'POST',
    url: '/patients/makedependent',
    data: {
      firstname,
      lastname,
      dob,
      accountId,
      patientId,
      email,
      phone,
      uuid,
    },
  });
};

export const useCreateDependent = () => {
  return useMutation(createDependent);
};

interface MakeIndepedentProp {
  firstname: string;
  lastname: string;
  accountId: number;
  email: string;
  phone: string;
  uuid: string;
  guardian_uuid: string;
  headerKeys: any;
  dob: any;
}

const makeInDependent = ({
  firstname,
  lastname,
  accountId,
  email,
  phone,
  uuid,
  guardian_uuid,
  headerKeys,
  dob,
}: MakeIndepedentProp) => {
  console.log(dob);
  return useAPI({
    method: 'POST',
    url: '/patients/makeindependent',
    headers: { ...headerKeys },
    data: {
      firstname,
      lastname,
      accountId,
      email,
      phone,
      uuid,
      guardian_uuid,
      dob,
    },
  });
};

export const usemakeInDependent = () => {
  return useMutation(makeInDependent);
};

const messageRequest = ({ request_id }: any) => {
  return useAPI({
    method: 'GET',
    url: `/messages/by-request/${request_id}`,
  });
};

export const useMessageRequest = ({ request_id }: any) => {
  return useQuery(
    [queryKeys.PATEINT_MESSAGES, request_id],
    () => messageRequest({ request_id })
    // { refetchInterval: 300 }
  );
};

interface SendMessage {
  content: string;

  parentId?: number | null;
  request_id: number;
}

const sendMessage = ({ content, parentId = null, request_id }: SendMessage) => {
  return useAPI({
    method: 'POST',
    url: '/messages/create',
    data: {
      content,
      parentId,

      request_id,
    },
  });
};

export const useSendMessage = () => {
  const query = useQueryClient();
  return useMutation(sendMessage, {
    onSuccess: () => {
      query.invalidateQueries([queryKeys.PATEINT_MESSAGES]);
    },
  });
};

const searchPatients = ({
  query,
  account_id,
}: {
  query: string;
  account_id: number;
}) => {
  return useAPI({
    method: 'GET',
    url: `patients/searchPatients?query=${query}&account_id=${account_id}`,
  });
};

export const useSearchPatients = ({
  query,
  account_id,
}: {
  query: string;
  account_id: number;
}) => {
  return useQuery([queryKeys.SEARCH_PATIENTS, query, account_id], () =>
    searchPatients({ query, account_id })
  );
};
